import { Fragment, useEffect, useRef, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { TableOfContentsEntry } from '@lexical/react/LexicalTableOfContentsPlugin';
import { TableOfContentsPlugin as LexicalTableOfContentsPlugin } from '@lexical/react/LexicalTableOfContentsPlugin';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import type { NodeKey } from 'lexical';

import { ReactComponent as ChevronSVG } from '../../images/icons/chevron-down.svg';

type StyledToggleButtonProps = {
  isOpened?: boolean;
};

const StyledTableOfContentsWrapper = styled('div')(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  color: '#65676b',
  padding: '10px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  maxHeight: '300px',
  '.heading2': {
    marginLeft: '10px',
  },
  '.heading3': {
    marginLeft: '20px',
  },
  '.selected-heading': {
    color: '#3578e5',
    position: 'relative',
  },
  '.selected-heading-wrapper::before': {
    content: '" "',
    position: 'absolute',
    display: 'inline-block',
    left: '-30px',
    top: '4px',
    zIndex: 10,
    height: '4px',
    width: '4px',
    backgroundColor: '#3578e5',
    border: 'solid 4px white',
    borderRadius: '50%',
  },
  '.normal-heading': {
    cursor: 'pointer',
    lineHeight: '20px',
    fontSize: '16px',
  },
  '.title': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '0 6px',
    cursor: 'pointer',
  },
  '.title button.opened': {
    transform: 'rotate(180deg)',
  },
  '.headings': {
    listStyle: 'none',
    marginTop: 0,
    marginLeft: 0,
    padding: '16px',
    height: '100%',
    '-msOverflowStyle': 'none',
    scrollbarWidth: 'none',
    columnGap: '20px',
    gridAutoFlow: 'column',
    columnCount: 1,
  },
  '.headings li': {
    position: 'relative',
    paddingLeft: '10px',
    marginBottom: '8px',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '16px',
    letterSpacing: '.4px',
  },
  '.headings li::before': {
    content: '"•"',
    position: 'absolute',
    left: 0,
    top: 0,
  },
  '.headings::-webkit-scrollbar': {
    display: 'none',
  },
}));

const StyledToggleButton = styled('button', {
  shouldForwardProp: propName => propName !== 'isOpened',
})<StyledToggleButtonProps>(({ theme, isOpened }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '24px',
  height: '24px',
  border: `1px solid ${theme.palette.neutral[300]}`,
  borderRadius: '50%',
  background: 'none',
  cursor: 'pointer',
  color: `${theme.palette.secondary.main}`,
  padding: 0,
  transform: isOpened ? `rotate(180deg)` : 'none',
}));

const MARGIN_ABOVE_EDITOR = 624;
const HEADING_WIDTH = 9;
const HEADING_ROW_ELEMENT_LIMIT = 10;

function isHeadingAtTheTopOfThePage(element: HTMLElement): boolean {
  const elementYPosition = element?.getClientRects()[0].y;
  return elementYPosition >= MARGIN_ABOVE_EDITOR && elementYPosition <= MARGIN_ABOVE_EDITOR + HEADING_WIDTH;
}
function isHeadingAboveViewport(element: HTMLElement): boolean {
  const elementYPosition = element?.getClientRects()[0].y;
  return elementYPosition < MARGIN_ABOVE_EDITOR;
}
function isHeadingBelowTheTopOfThePage(element: HTMLElement): boolean {
  const elementYPosition = element?.getClientRects()[0].y;
  return elementYPosition >= MARGIN_ABOVE_EDITOR + HEADING_WIDTH;
}

const TableOfContentsList = ({ tableOfContents }: { tableOfContents: TableOfContentsEntry[] }): JSX.Element => {
  const { t } = useTranslation('text-editor');
  const [selectedKey, setSelectedKey] = useState('');
  const [isOpened, setIsOpened] = useState(false);
  const selectedIndex = useRef(0);
  const [editor] = useLexicalComposerContext();

  function scrollToNode(key: NodeKey, currIndex: number) {
    editor.getEditorState().read(() => {
      const domElement = editor.getElementByKey(key);
      if (domElement !== null) {
        domElement.scrollIntoView();
        setSelectedKey(key);
        selectedIndex.current = currIndex;
      }
    });
  }

  useEffect(() => {
    function scrollCallback() {
      if (tableOfContents.length !== 0 && selectedIndex.current < tableOfContents.length - 1) {
        let currentHeading = editor.getElementByKey(tableOfContents[selectedIndex.current][0]);
        if (currentHeading !== null) {
          if (isHeadingBelowTheTopOfThePage(currentHeading)) {
            while (
              currentHeading !== null &&
              isHeadingBelowTheTopOfThePage(currentHeading) &&
              selectedIndex.current > 0
            ) {
              const prevHeading = editor.getElementByKey(tableOfContents[selectedIndex.current - 1][0]);
              if (
                prevHeading !== null &&
                (isHeadingAboveViewport(prevHeading) || isHeadingBelowTheTopOfThePage(prevHeading))
              ) {
                selectedIndex.current--;
              }
              currentHeading = prevHeading;
            }
            const prevHeadingKey = tableOfContents[selectedIndex.current][0];
            setSelectedKey(prevHeadingKey);
          } else if (isHeadingAboveViewport(currentHeading)) {
            while (
              currentHeading !== null &&
              isHeadingAboveViewport(currentHeading) &&
              selectedIndex.current < tableOfContents.length - 1
            ) {
              const nextHeading = editor.getElementByKey(tableOfContents[selectedIndex.current + 1][0]);
              if (
                nextHeading !== null &&
                (isHeadingAtTheTopOfThePage(nextHeading) || isHeadingAboveViewport(nextHeading))
              ) {
                selectedIndex.current++;
              }
              currentHeading = nextHeading;
            }
            const nextHeadingKey = tableOfContents[selectedIndex.current][0];
            setSelectedKey(nextHeadingKey);
          }
        }
      } else {
        selectedIndex.current = 0;
      }
    }
    let timerId: ReturnType<typeof setTimeout>;

    function debounceFunction(func: () => void, delay: number) {
      clearTimeout(timerId);
      timerId = setTimeout(func, delay);
    }

    function onScroll(): void {
      debounceFunction(scrollCallback, 10);
    }

    document.addEventListener('scroll', onScroll);
    return () => document.removeEventListener('scroll', onScroll);
  }, [tableOfContents, editor]);

  return tableOfContents.length ? (
    <StyledTableOfContentsWrapper>
      <div onClick={() => setIsOpened(!isOpened)} className="title">
        <span>{t('toolbar.tableOfContents')}</span>
        <StyledToggleButton type="button" isOpened={isOpened}>
          <ChevronSVG />
        </StyledToggleButton>
      </div>
      {isOpened && (
        <ul
          className="headings"
          style={{
            display: tableOfContents.length > HEADING_ROW_ELEMENT_LIMIT ? 'grid' : 'block',
            gridTemplateRows: `repeat(${HEADING_ROW_ELEMENT_LIMIT}, 1fr)`,
          }}
        >
          {tableOfContents.map(([key, text], index) => {
            return (
              <div
                className={`normal-heading-wrapper ${selectedKey === key ? 'selected-heading-wrapper' : ''}`}
                key={key}
              >
                <div onClick={() => scrollToNode(key, index)} role="button" tabIndex={0}>
                  <li
                    className={`normal-heading ${selectedKey === key ? 'selected-heading' : ''}
                    `}
                  >
                    {('' + text).length > 27 ? text.substring(0, 27) + '...' : text}
                  </li>
                </div>
              </div>
            );
          })}
        </ul>
      )}
    </StyledTableOfContentsWrapper>
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <Fragment></Fragment>
  );
};

export const TableOfContentsPlugin = () => {
  return (
    <LexicalTableOfContentsPlugin>
      {tableOfContents => {
        const tableOfContentsH1Only = tableOfContents.filter(([, , tag]) => tag === 'h1');
        return <TableOfContentsList tableOfContents={tableOfContentsH1Only} />;
      }}
    </LexicalTableOfContentsPlugin>
  );
};
